import React, { FC, useState } from 'react';
import { useTranslate, usePermissions } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Accordion from '@material-ui/core/Accordion';
import { withStyles } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Alert } from '@material-ui/lab';
import { isNil, uniqBy } from 'lodash';
import {
  AmountField,
  AccordionSummary,
  AccordionDetails,
  H2,
  H3,
  ReferenceField,
  TextField,
  GlTypeLabel,
} from '~/components';
import { Section } from '~/layout';
import TableCell from '~/components/TableCell';
import BalancesTable from '~/components/ra/Accounts/BalancesTable';
import { HeadTransaction, TransferEffect } from '~/types/HeadTransaction';
import { Account } from '~/types/account';
import OperationTypeField from '~/components/OperationTypeField';
import { Longdash } from '~/utils';

export const l = (key: string): string => `resources.headTransactions.show.processing.${key}`;

const isCounterAccount = (account: Account): boolean =>
  account.productTemplate?.category === 'CNL_ACCOUNT';

const pricingKey = (e: TransferEffect): string =>
  `${e.transferId}-${e.pricingId}-${e.pricingRevision}`;

const AccountEffectDetails: FC<{ account: Account; trx: HeadTransaction }> = ({ account, trx }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string) => translate(l(key));
  const { permissions } = usePermissions();

  const accountEffect = trx.accountEffects?.find((e) => e.accountId === account.id);
  const balances = account.balances || [];
  const selectedPricings = uniqBy(
    (trx.transferEffects || []).filter((e) => e.accountId === account.id && e.pricingId),
    pricingKey
  );
  const counterEffects = (trx.counterEffects || []).filter((e) => e.accountId === account.id);

  const transferLink = (transferId: string) =>
    permissions?.includes('transaction.transfers.view')
      ? `/transaction/transfers/${transferId}/show`
      : undefined;

  const totalBalanceEffect = isCounterAccount(account)
    ? undefined
    : accountEffect?.totalBalanceEffect;

  return (
    <div>
      {!isNil(totalBalanceEffect) && (
        <div className={classes.block}>
          <TextField label={t('totalBalanceEffect')}>
            <AmountField amount={{ value: totalBalanceEffect, currency: account.currency }} />
          </TextField>
        </div>
      )}

      {balances.length > 0 && (
        <div className={classes.block}>
          <H3 noPaddingLeft topSpacing={0}>
            {t('balancesAfterPosting')}
          </H3>
          <div className={classes.balancesTable}>
            <BalancesTable
              balances={balances}
              withHeader={false}
              accountProfileType={account.profileType}
              fullWidth
            />
          </div>
        </div>
      )}

      {selectedPricings.length > 0 && (
        <div className={classes.block}>
          <H3 noPaddingLeft topSpacing={0}>
            {t('selectedPricing._')}
          </H3>
          <Table className={classes.pricingTable}>
            <TableHead>
              <TableRow>
                <TableCell>{t('selectedPricing.transfer')}</TableCell>
                <TableCell>{t('selectedPricing.pricing')}</TableCell>
                <TableCell>{t('selectedPricing.revision')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedPricings.map((effect) => {
                const transfer = effect.transferPublicId || effect.transferId;
                return (
                  <TableRow key={pricingKey(effect)}>
                    <TableCell>
                      <TextField
                        showLink={effect.transferPublicId && transferLink(effect.transferPublicId)}
                        copyable={transfer}
                        title={transfer}
                      >
                        <div className={classes.ellipsis}>{transfer}</div>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <ReferenceField
                        record={{ id: effect.pricingId as string, type: 'PRICING' }}
                        withLabel={false}
                      />
                    </TableCell>
                    <TableCell>
                      {isNil(effect.pricingRevision) ? Longdash : `#${effect.pricingRevision}`}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {counterEffects.length > 0 && (
        <div className={classes.block}>
          <H3 noPaddingLeft topSpacing={0}>
            {t('counterEffects._')}
          </H3>
          <Table className={classes.countersTable}>
            <TableHead>
              <TableRow>
                <TableCell>{t('counterEffects.code')}</TableCell>
                <TableCell>{t('counterEffects.difference')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {counterEffects.map((effect, index) => (
                <TableRow key={`${effect.code}-${index}`}>
                  <TableCell>{effect.code}</TableCell>
                  <TableCell>{effect.difference}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

const hasEffectsToShow = (trx: HeadTransaction, account: Account): boolean =>
  (account.balances?.length ?? 0) > 0 ||
  (trx.counterEffects || []).some((e) => e.accountId === account.id) ||
  (trx.transferEffects || []).some((e) => e.accountId === account.id && e.pricingId) ||
  (!isCounterAccount(account) &&
    (trx.accountEffects || []).some((e) => e.accountId === account.id));

const AccountsWithAlias: FC<{ accounts: Account[]; trx: HeadTransaction }> = ({
  accounts,
  trx,
}) => {
  const [expanded, setExpanded] = useState<{ [x: string]: boolean }>({});

  return (
    <div>
      {accounts.map((account, index) => {
        const { number, alias, glType } = account;
        const key = account.id as string;
        const isExpanded = Boolean(expanded[key]);
        const heading = alias ? `${number} (${alias})` : number;
        return (
          <Grid key={index} item xs={12}>
            <Accordion
              expanded={isExpanded}
              onChange={() => setExpanded((prev) => ({ ...prev, [key]: !isExpanded }))}
            >
              <AccordionSummaryStyled
                aria-controls={`${key}-content`}
                id={`${key}-header`}
                expanded={isExpanded}
                heading={
                  <TextField showLink={`/accounts/${account.id}/show`} copyable={number}>
                    <GlTypeLabel glType={glType} text={heading} />
                  </TextField>
                }
              />
              <AccordionDetails>
                {isExpanded && <AccountEffectDetails account={account} trx={trx} />}
              </AccordionDetails>
            </Accordion>
          </Grid>
        );
      })}
    </div>
  );
};

interface Props {
  record?: HeadTransaction;
  [x: string]: any;
}

const ProcessingTab: FC<Props> = (props) => {
  const { record } = props;
  const translate = useTranslate();
  const t = (key: string) => translate(l(key));
  const td = (key: string) => translate(`resources.headTransactions.show.details.${key}`);
  const classes = useStyles();

  if (!record) return null;
  const trx = record as HeadTransaction;

  const accountsWithEffects = (trx.accounts || []).filter((a) => hasEffectsToShow(trx, a));

  return (
    <Section noPaddings>
      {/* Transfer Plan */}
      <Grid container className={classes.bottomSpacing}>
        <Grid item xs={12}>
          <H2 topSpacing={0} bottomSpacing={2}>
            {t('transferPlan')}
          </H2>
          <Alert severity='info' className={classes.disclaimer}>
            <Typography variant='body2'>{t('disclaimer')}</Typography>
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <Section fullWidth noPaddings>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{td('transfer.amount')}</TableCell>
                  <TableCell>{td('transfer.from')}</TableCell>
                  <TableCell>{td('transfer.to')}</TableCell>
                  <TableCell>{td('transfer.type')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trx.transfers.map((transfer) => (
                  <TableRow key={transfer.id}>
                    <TableCell>
                      <AmountField amount={transfer.amount} />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const a = trx.accounts.find((a) => a.id === transfer.from);
                        if (!a) return Longdash;
                        return (
                          <TextField
                            labelStyle={{ fontSize: 11, opacity: 0.5 }}
                            label={a?.alias}
                            showLink={`/accounts/${a?.id}/show`}
                            copyable={typeof a?.number === 'string' ? a.number : false}
                          >
                            <GlTypeLabel glType={a?.glType} text={a?.number} />
                          </TextField>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const a = trx.accounts.find((a) => a.id === transfer.to);
                        if (!a) return Longdash;
                        return (
                          <TextField
                            labelStyle={{ fontSize: 11, opacity: 0.5 }}
                            label={a?.alias}
                            showLink={`/accounts/${a?.id}/show`}
                            copyable={typeof a?.number === 'string' ? a.number : false}
                          >
                            <GlTypeLabel glType={a?.glType} text={a?.number} />
                          </TextField>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <OperationTypeField operationType={transfer.operationType} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </Grid>
      </Grid>

      {/* Account effects */}
      {accountsWithEffects.length > 0 && (
        <Grid container className={classes.bottomSpacing}>
          <Grid item xs={12}>
            <H2 topSpacing={2} bottomSpacing={2}>
              {t('accountEffects')}
            </H2>
          </Grid>
          <Grid item xs={12}>
            <AccountsWithAlias accounts={accountsWithEffects} trx={trx} />
          </Grid>
        </Grid>
      )}
    </Section>
  );
};

const AccordionSummaryStyled = withStyles({
  root: {
    '&.Mui-focused': {
      backgroundColor: 'inherit',
    },
  },
})((props: any) => <AccordionSummary {...props} />);

const VALUE_COLUMN_WIDTH = '20%';
const TRANSFER_COLUMN_WIDTH = '40%';

const useStyles = makeStyles((theme) => ({
  bottomSpacing: {
    paddingBottom: theme.spacing(2),
  },
  disclaimer: {
    marginBottom: theme.spacing(2),
  },
  block: {
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  balancesTable: {
    '& table': {
      tableLayout: 'fixed',
    },
    '& th:last-child, & td:last-child': {
      width: VALUE_COLUMN_WIDTH,
    },
  },
  ellipsis: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pricingTable: {
    tableLayout: 'fixed',
    '& th:first-child, & td:first-child': {
      width: TRANSFER_COLUMN_WIDTH,
    },
    '& th:last-child, & td:last-child': {
      width: VALUE_COLUMN_WIDTH,
    },
  },
  countersTable: {
    tableLayout: 'fixed',
    '& th:last-child, & td:last-child': {
      width: VALUE_COLUMN_WIDTH,
    },
    '& td': {
      overflowWrap: 'anywhere',
    },
  },
}));

export default ProcessingTab;
